import type { Note } from '@oasis/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Hourglass, NotebookPen, Plus } from 'lucide-react';
import { useState, type ReactElement, type SyntheticEvent } from 'react';

import { useApi, useApiQuery } from '../../api.js';
import {
  ActionBar,
  Badge,
  Button,
  DataTable,
  Drawer,
  EmptyState,
  ErrorMessage,
  Flash,
  Loading,
  PageHeader,
  StatusBadge,
  TextInput,
  useFlash,
  type Column,
} from '../../design/components.js';

const NOTES = '/example/notes?limit=50';

const COLUMNS: Column<Note>[] = [
  { key: 'title', header: 'Titre', primary: true, sortValue: (n) => n.title },
  {
    key: 'created',
    header: 'Créée le',
    sortValue: (n) => n.created_at,
    render: (n) =>
      new Date(n.created_at).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }),
  },
  {
    key: 'status',
    header: 'État',
    sortValue: (n) => (n.processed_at === null ? 0 : 1),
    render: (n) =>
      n.processed_at === null ? (
        <StatusBadge status="attente" label="En cours de traitement" />
      ) : (
        <StatusBadge status="termine" label="Traitée" />
      ),
  },
];

/** Écran du module exemple : mes notes (liste paginée), création en tiroir, état « traitée ». */
export function NotesRoute(): ReactElement {
  const call = useApi();
  const client = useQueryClient();
  const notes = useApiQuery<Note[]>(NOTES, 5_000);
  const [title, setTitle] = useState('');
  const [open, setOpen] = useState(false);
  const flash = useFlash();
  const create = useMutation({
    mutationFn: (value: string) =>
      call<Note>('/example/notes', { method: 'POST', body: JSON.stringify({ title: value }) }),
    onSuccess: async (_note, value) => {
      setTitle('');
      setOpen(false);
      flash.show(`Note « ${value} » créée.`);
      await client.invalidateQueries({ queryKey: [NOTES] });
    },
  });

  const submit = (event: SyntheticEvent): void => {
    event.preventDefault();
    create.mutate(title);
  };
  const all = notes.data ?? [];
  const waiting = all.filter((n) => n.processed_at === null).length;
  const nouvelle = (): void => {
    setOpen(true);
  };

  return (
    <div className="o-stack">
      <PageHeader
        title="Mes notes"
        description="Module exemple : démonstration de l'architecture."
        actions={
          <ActionBar
            primary={
              <Button variant="primary" onClick={nouvelle}>
                <Plus aria-hidden="true" size={18} /> Nouvelle note
              </Button>
            }
          />
        }
      >
        {notes.isSuccess && all.length > 0 && (
          <span className="o-row">
            <Badge>
              <NotebookPen aria-hidden="true" size={12} /> {all.length} note(s)
            </Badge>
            {waiting > 0 && (
              <Badge tone="info">
                <Hourglass aria-hidden="true" size={12} /> {waiting} en cours de traitement
              </Badge>
            )}
          </span>
        )}
      </PageHeader>
      <Flash message={flash.message} onClose={flash.clear} />
      {notes.isPending && <Loading />}
      {notes.isError && <ErrorMessage error={notes.error} />}
      {notes.isSuccess && (
        <DataTable
          caption="Mes notes"
          columns={COLUMNS}
          rows={all}
          rowKey={(n) => n.id}
          initialSort={{ key: 'created', direction: 'desc' }}
          empty={
            <EmptyState
              title="Aucune note pour l'instant"
              icon={NotebookPen}
              action={
                <Button variant="primary" onClick={nouvelle}>
                  Nouvelle note
                </Button>
              }
            />
          }
        />
      )}
      <Drawer
        open={open}
        onOpenChange={setOpen}
        title="Nouvelle note"
        footer={
          <>
            <Button
              onClick={() => {
                setOpen(false);
              }}
            >
              Annuler
            </Button>
            <Button type="submit" form="o-form-note" variant="primary" disabled={create.isPending}>
              Créer la note
            </Button>
          </>
        }
      >
        <form id="o-form-note" onSubmit={submit} className="o-stack o-stack--tight">
          <TextInput
            label="Titre"
            value={title}
            maxLength={200}
            required
            onChange={(e) => {
              setTitle(e.target.value);
            }}
          />
        </form>
        {create.isError && <ErrorMessage error={create.error} />}
      </Drawer>
    </div>
  );
}
